import { useState, useCallback, useEffect } from 'react'
import { useIsMobile } from '../../hooks/useIsMobile'
import '@szhsin/react-menu/dist/core.css'
import {
  FilterConfig,
  FilterViewConfig,
  GroupConfig,
  GroupByOption,
  Level,
  SortConfig,
  SortDirection,
  TaskState,
} from './types'
import { useFilteredNotes } from './hooks/useFilteredNotes'
import ItemList from './components/ItemList'
import UnifiedFilterDropdown from './components/UnifiedFilterDropdown'
import { TASK_LIMIT } from './utils'

// Default filter configuration
const defaultFilterConfig: FilterConfig = {
  noteType: undefined,
  taskState: [TaskState.OPEN],
  dateRange: { type: 'thisMonth' },
  folder: undefined,
  searchTerm: '',
  level: Level.TASKS,
}

// Default sort configuration
const defaultSortConfig: SortConfig = {
  direction: 'asc',
}

// Default group configuration
const defaultGroupConfig: GroupConfig = {
  option: GroupByOption.NOTE_PATH,
}

// Default filter view configuration
const defaultFilterViewConfig: FilterViewConfig = {
  filters: defaultFilterConfig,
  sort: defaultSortConfig,
  group: defaultGroupConfig,
}

// Key for localStorage
const FILTER_STORAGE_KEY = 'noteplan-filter-settings'

// Helper function to load filter config from localStorage
function loadFilterConfigFromStorage(): FilterViewConfig {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as FilterViewConfig
      // Validate that the stored config has the expected structure
      if (
        typeof parsed === 'object' &&
        'filters' in parsed &&
        'sort' in parsed &&
        'group' in parsed
      ) {
        // Use saved config as-is, only filling in missing structural properties
        // Don't apply defaults to individual filter properties - let them be undefined
        const mergedFilters: FilterConfig = {
          noteType:
            'noteType' in parsed.filters ? parsed.filters.noteType : undefined,
          taskState:
            'taskState' in parsed.filters
              ? parsed.filters.taskState
              : undefined,
          checklistState:
            'checklistState' in parsed.filters
              ? parsed.filters.checklistState
              : undefined,
          dateRange:
            'dateRange' in parsed.filters
              ? parsed.filters.dateRange
              : undefined,
          folder:
            'folder' in parsed.filters ? parsed.filters.folder : undefined,
          searchTerm:
            'searchTerm' in parsed.filters ? parsed.filters.searchTerm : '',
          level: 'level' in parsed.filters ? parsed.filters.level : Level.TASKS,
        }

        return {
          filters: mergedFilters,
          sort: { ...defaultSortConfig, ...parsed.sort },
          group: { ...defaultGroupConfig, ...parsed.group },
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load filter settings from localStorage:', error)
  }
  // Only use full defaults when localStorage is empty or corrupted (first-time user)
  return defaultFilterViewConfig
}

// Helper function to save filter config to localStorage
function saveFilterConfigToStorage(config: FilterViewConfig) {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(config))
  } catch (error) {
    console.warn('Failed to save filter settings to localStorage:', error)
  }
}

export default function FilterView({
  showCalendar,
  toggleCalendar,
}: {
  showCalendar: boolean
  toggleCalendar: () => void
}) {
  const isMobile = useIsMobile()
  const [filterConfig, setFilterConfig] = useState<FilterViewConfig>(() =>
    loadFilterConfigFromStorage()
  )

  // Local search term state for immediate UI feedback
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    const loadedConfig = loadFilterConfigFromStorage()
    return loadedConfig.filters.searchTerm ?? ''
  })

  // Task limit state - starts at TASK_LIMIT and increases by TASK_LIMIT each time
  const [currentTaskLimit, setCurrentTaskLimit] = useState(TASK_LIMIT)

  // Debounced effect to update filter config when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilterConfig((previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          searchTerm: searchTerm,
        },
      }))
    }, 350)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  // Effect to save filter config to localStorage whenever it changes
  useEffect(() => {
    saveFilterConfigToStorage(filterConfig)
    // Reset task limit when filters change
    setCurrentTaskLimit(TASK_LIMIT)
  }, [filterConfig])

  // Handle search term changes
  const handleSearchTermChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm) // Immediate UI update
  }, [])

  // Sort actions
  const setSortDirection = useCallback((direction: SortDirection) => {
    setFilterConfig((previous) => ({
      ...previous,
      sort: {
        ...previous.sort,
        direction,
      },
    }))
  }, [])

  // Get filtered notes with current task limit
  const filteredNotesResult = useFilteredNotes(filterConfig.filters, currentTaskLimit)

  // Handle load more tasks
  const handleLoadMoreTasks = useCallback(() => {
    setCurrentTaskLimit(previous => previous + TASK_LIMIT)
  }, [])

  return (
    <div className='flex h-full w-full flex-col'>
      {/* Header */}
      <div className='relative border-b p-2'>
        {isMobile && (
          <button
            onClick={toggleCalendar}
            className='absolute left-1.5 top-2 bg-transparent text-lg transition md:hidden'
          >
            {showCalendar ? (
              <i className='fa-regular fa-angle-up'></i>
            ) : (
              <i className='fa-solid fa-angle-down'></i>
            )}
          </button>
        )}
        <h1 className='text-center text-xl font-semibold md:text-left'>
          Filter
        </h1>

        {/* Filter Controls */}
        <div className='mt-4 flex flex-col gap-3 md:flex-row md:items-center'>
          {/* Mobile: Filter and Sort side by side (50/50) */}
          <div className='flex gap-3 md:flex-1 md:items-center md:justify-start'>
            {/* Unified Filter Dropdown */}
            <div className='flex-1 md:flex-none'>
              <UnifiedFilterDropdown
                filterConfig={filterConfig.filters}
                onFilterChange={(filters) => {
                  setFilterConfig((previous) => ({
                    ...previous,
                    filters: { ...previous.filters, ...filters },
                  }))
                }}
              />
            </div>

            {/* Sort Direction */}
            <div className='flex flex-1 items-center space-x-2 md:ml-4 md:flex-none'>
              <span className='whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300'>
                Sort:
              </span>
              <select
                className='min-h-[44px] w-full rounded-md border border-gray-300 px-2 py-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 md:min-h-[32px] md:w-auto md:py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                value={filterConfig.sort.direction}
                onChange={(event) => {
                  setSortDirection(event.target.value as SortDirection)
                }}
              >
                <option value='asc'>Ascending</option>
                <option value='desc'>Descending</option>
              </select>
            </div>
          </div>

          {/* Search Input - Full width on mobile, flexible on desktop */}
          <div className='flex items-center md:ml-4 md:max-w-md md:flex-1'>
            <input
              type='text'
              className='min-h-[44px] w-full rounded-md border border-gray-300 px-3 py-3 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 md:min-h-[32px] md:py-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
              placeholder='Search notes and tasks...'
              value={searchTerm}
              spellCheck={false}
              autoCorrect='off'
              autoComplete='off'
              autoCapitalize='off'
              onChange={(event) => {
                handleSearchTermChange(event.target.value)
              }}
            />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className='flex flex-1 overflow-auto px-4'>
        <ItemList
          filteredNotesResult={filteredNotesResult}
          sortConfig={filterConfig.sort}
          groupConfig={filterConfig.group}
          filterConfig={filterConfig.filters}
          onLoadMoreTasks={handleLoadMoreTasks}
          currentTaskLimit={currentTaskLimit}
        />
      </div>
    </div>
  )
}
